import React, { Suspense, lazy } from 'react';
import ConfirmModal from './ConfirmModal';
import { ModalLoadingShim } from '../ui/LoadingShim';
import { SyncFolderService } from '../../services/sync-folder';
import { storePhotoBlob } from '../../services/photo-blob-store';
import { executeFolderReconnect } from '../../services/folder-reconnect-runner';
import {
    captureProjectRunContext,
    isProjectRunContextCurrent,
} from '../../utils/project-run-context';

const ReconnectFolderModal = lazy(() => import('./ReconnectFolderModal'));
const FolderReconnectBanner = lazy(() => import('../ui/FolderReconnectBanner'));

export interface AppAuxiliaryModalsContainerProps {
    // ConfirmModal
    confirmModal: {
        isOpen: boolean;
        onConfirm: () => void;
        onCancel?: () => void;
        title?: string;
        message?: string;
        confirmText?: string;
        cancelText?: string;
        confirmColor?: string;
        icon?: any;
        zIndex?: number;
        defaultFocus?: 'cancel' | 'confirm' | 'dialog';
        hint?: string;
    };
    closeConfirm: () => void;

    // FolderReconnectBanner
    mediaRecoveryNeeded: any;
    setMediaRecoveryNeeded: (needed: any) => void;
    currentProjectIdRef: React.MutableRefObject<string | number | null>;
    projectLoadGenerationRef: React.MutableRefObject<number>;
    projectPersistenceAbortRef: React.MutableRefObject<AbortController | null>;
    projects: any[];
    photos: any[];
    documents: any[];
    setDocuments: React.Dispatch<React.SetStateAction<any[]>>;
    setPhotos: React.Dispatch<React.SetStateAction<any[]>>;

    // ReconnectFolderModal
    reconnectModalOpen: boolean;
    setReconnectModalOpen: (open: boolean) => void;
    pendingProjectLoad: any;
    setPendingProjectLoad: (load: any) => void;
    FileSystemService: any;
    setParentFolder: (folder: any) => void;
    setQuoteSubfolder: (subfolder: any) => void;
    setMissingFiles: (files: any) => void;
    setFolderFiles: (files: any) => void;
    completeProjectLoad: (project: any) => void;
}

export const AppAuxiliaryModalsContainer: React.FC<AppAuxiliaryModalsContainerProps> = ({
    confirmModal,
    closeConfirm,

    mediaRecoveryNeeded,
    setMediaRecoveryNeeded,
    currentProjectIdRef,
    projectLoadGenerationRef,
    projectPersistenceAbortRef,
    projects,
    photos,
    documents,
    setDocuments,
    setPhotos,

    reconnectModalOpen,
    setReconnectModalOpen,
    pendingProjectLoad,
    setPendingProjectLoad,
    FileSystemService,
    setParentFolder,
    setQuoteSubfolder,
    setMissingFiles,
    setFolderFiles,
    completeProjectLoad,
}) => {
    // 5A / O-S1: a missing-files notice names the project that raised it. Show it only over that project, so a notice the reset or a switch
    // did not clear (or one republished late) can never sit over a different or blank project.
    const recoveryNoticeOwned = !!mediaRecoveryNeeded
        && String(mediaRecoveryNeeded.projectId ?? '') === String(currentProjectIdRef.current ?? '');
    return (
        <>
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                confirmColor={confirmModal.confirmColor}
                icon={confirmModal.icon}
                zIndex={confirmModal.zIndex}
                defaultFocus={confirmModal.defaultFocus}
                hint={confirmModal.hint}
            />

            {recoveryNoticeOwned && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 8500, pointerEvents: 'none' }}>
                    <div style={{ maxWidth: '960px', margin: '0 auto', pointerEvents: 'auto' }}>
                        <Suspense fallback={null}>
                            <FolderReconnectBanner
                                summary={mediaRecoveryNeeded}
                                onDismiss={() => setMediaRecoveryNeeded(null)}
                                onReconnected={async () => {
                                    const recoveryProjectId = String(currentProjectIdRef.current || '');
                                    const recoveryOwnerValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
                                        .test(recoveryProjectId);
                                    if (!recoveryOwnerValid) return false;
                                    const recoveryRunContext = captureProjectRunContext({
                                        projectId: recoveryProjectId,
                                        loadGeneration: projectLoadGenerationRef.current,
                                        signal: projectPersistenceAbortRef.current?.signal || null,
                                    });
                                    const recoveryIsCurrent = () => isProjectRunContextCurrent(recoveryRunContext, {
                                        projectId: currentProjectIdRef.current,
                                        loadGeneration: projectLoadGenerationRef.current,
                                        signal: projectPersistenceAbortRef.current?.signal || null,
                                    });
                                    let recoveredAny = false;
                                    try {
                                        const activeProject = recoveryProjectId
                                            ? projects.find(p => p.id === recoveryProjectId)
                                            : null;
                                        const recovered = await SyncFolderService.recoverMissingFiles(
                                            activeProject || {},
                                            photos,
                                            documents,
                                            [],
                                        );
                                        if (!recoveryIsCurrent()) return false;
                                        if (recovered?.documents?.length && recoveryIsCurrent()) {
                                            recoveredAny = true;
                                            setDocuments(prev => recoveryIsCurrent() ? prev.map((doc, index) => {
                                                const match = recovered.documents.find(d => d.index === index);
                                                return match ? {
                                                    ...doc,
                                                    pdfDataUrl: match.pdfDataUrl,
                                                    pdfBlob: match.pdfBlob,
                                                    syncFileName: match.syncFileName,
                                                    pdfPreviewUnavailableReason: undefined,
                                                } : doc;
                                            }) : prev);
                                        }
                                        if (recovered?.photos?.length) {
                                            const photoUpdates: any[] = [];
                                            for (const item of recovered.photos) {
                                                if (!recoveryIsCurrent()) return false;
                                                try {
                                                    const blobId = await storePhotoBlob(item.blob, recoveryProjectId, {
                                                        width: item.meta?.width,
                                                        height: item.meta?.height,
                                                        recoveredFrom: 'banner-reconnect',
                                                    });
                                                    if (!recoveryIsCurrent()) return false;
                                                    photoUpdates.push({ index: item.index, blobId, sizeKB: Math.round((item.blob?.size || 0) / 1024) });
                                                } catch (recoveryErr: any) {
                                                    console.warn('[QM] Photo recovery blob persist failed:', recoveryErr?.message);
                                                }
                                            }
                                            if (photoUpdates.length && recoveryIsCurrent()) {
                                                recoveredAny = true;
                                                setPhotos(prev => recoveryIsCurrent() ? prev.map((photo, index) => {
                                                    const match = photoUpdates.find(p => p.index === index);
                                                    return match ? { ...photo, blobId: match.blobId, sizeKB: match.sizeKB || photo.sizeKB, data: undefined, dataUrl: undefined, _dataLost: false } : photo;
                                                }) : prev);
                                            }
                                        }
                                    } catch (err: any) {
                                        console.warn('[QM] Banner-triggered recovery failed:', err?.message);
                                    }
                                    if (recoveredAny && recoveryIsCurrent()) {
                                        setMediaRecoveryNeeded(null);
                                    }
                                    return recoveredAny;
                                }}
                            />
                        </Suspense>
                    </div>
                </div>
            )}

            {reconnectModalOpen && (
                <Suspense fallback={<ModalLoadingShim />}>
                    <ReconnectFolderModal
                        isOpen={reconnectModalOpen}
                        onClose={() => {
                            setReconnectModalOpen(false);
                            setPendingProjectLoad(null);
                        }}
                        folderName={pendingProjectLoad?.projectFolder?.parentName || pendingProjectLoad?.projectFolder?.name || ''}
                        subfolderName={pendingProjectLoad?.projectFolder?.subfolderName || null}
                        onReconnect={(result: any) => executeFolderReconnect({
                            result,
                            pendingProjectLoad,
                            FileSystemService,
                            setParentFolder,
                            setQuoteSubfolder,
                            setMissingFiles,
                            setPhotos,
                            setDocuments,
                            setFolderFiles,
                            setPendingProjectLoad,
                            setReconnectModalOpen,
                            completeProjectLoad,
                        })}
                        onSkip={() => {
                            setReconnectModalOpen(false);
                            completeProjectLoad(pendingProjectLoad);
                            setPendingProjectLoad(null);
                        }}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AppAuxiliaryModalsContainer;
